// eslint-disable-next-line import/no-extraneous-dependencies, no-unused-vars
import React, { memo, useCallback } from 'react';
import { createContext } from 'use-context-selector';
import noop from 'lodash/noop';
import { node } from 'prop-types';
// import { useTranslation } from 'react-i18next';

import useRouter from 'Hooks/useRouter';
import fetch from 'Utils/fetch';

export const FetchContext = createContext(noop);

// De-dupe identical in-flight GET requests: while a request for a given path is
// pending, any concurrent caller for the SAME path reuses that single request
// (and its parsed result) instead of firing another. It resolves immediately —
// there is no artificial delay. Distinct paths (e.g. different `?page=`) are
// never shared, and non-GET requests bypass this entirely. Lower-level
// cancellation of duplicate in-flight GETs still lives in Utils/fetch.
const inFlightGets = new Map();
const dedupedFetch = (key, catchHandler, ...args) => {
  const existing = inFlightGets.get(key);
  if (existing) return existing;

  const request = fetch(...args)
    .catch(catchHandler)
    .finally(() => {
      inFlightGets.delete(key);
    });

  inFlightGets.set(key, request);
  return request;
};

const FetchProvider = memo(({ children }) => {
  const { replace } = useRouter();
  // const { t } = useTranslation();

  const catchHandler = useCallback(
    err => {
      if (err.code === 20) return null;

      // todo: should use router location ?
      if (err.status === 401 || err.status === 403) {
        replace({
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          state: {
            error: err.message || JSON.stringify(err),
            status: err.status,
          }
        });
      } else if (err.location) {
        // TODO: can this be treated ?
        window.location.href = err.location;
      } else if (err.status === 404) {
        // TODO: treat 404
        console.warn(`Not Found`, err);
        // replace('/not-found');
      } else if (err.status >= 500) {
        // TODO: treat 5xx
        replace({
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          state: {
            error: err.message || JSON.stringify(err),
            status: 500,
            networkError: err.info?.message || err.message
          }
        });
      }

      if (err.message.includes('An error occurred while fetching the data.')) {
        // err.message = t('common.apiError');
        // eslint-disable-next-line no-param-reassign
        // err.message = 'Unable to reach datacenter. Please try again in a few moments !';
      }

      throw err;
    },
    []
  );

  const fetchWrapper = useCallback(
    (...args) => {
      const path = args?.at(0);
      const opts = args?.at(1);
      if (
        typeof path === 'string'
        && (
          !opts?.method
          || opts.method === 'GET'
        )
      ) {
        // Key by the full path (query string included) so distinct requests
        // stay distinct; only truly identical in-flight GETs are collapsed.
        return dedupedFetch(path, catchHandler, ...args);
      }
      return fetch(...args).catch(catchHandler);
    },
    [catchHandler]
  );

  return (
    <FetchContext.Provider value={ fetchWrapper }>
      { children }
    </FetchContext.Provider>
  );
});

FetchProvider.propTypes = {
  children: node
};
FetchProvider.displayName = 'FetchProvider';

export default FetchProvider;
